import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { canEditSection } from '@/lib/access';
import { useCycle } from '@/lib/cycle';
import { PageHeader, Card, Loading, Empty, SectionBadge } from '@/components/UI';
import { formatNumber, formatDateTime } from '@/lib/format';
import { Upload, AlertTriangle, FileSpreadsheet, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = '/supply-chain/schedule-vs-supply';

// The parser/backend uses human-readable unit names ("Unit-1", "Unit-3",
// "Sanber Labs"). The global MRM unit selector exposes the internal code
// (U1 / U3 / SL). Translate so this screen can ride the same global
// selector that drives every other MRM page — without a local toggle.
const CODE_TO_PARSER_UNIT = { U1: 'Unit-1', U3: 'Unit-3', SL: 'Sanber Labs' };

const formatRupees = (n) => {
  if (n === null || n === undefined) return '–';
  return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

export default function ScheduleVsSupply() {
  const { user } = useAuth();
  const { currentUnit, currentCycle } = useCycle();
  const canUpload = canEditSection(user, 'schedule_vs_supply');
  const isAdmin = user?.role === 'admin';
  const selectedUnitName = currentUnit ? CODE_TO_PARSER_UNIT[currentUnit.code] : null;
  // YYYY-MM for the cycle month — same source as the topbar month selector.
  const currentCyclePeriod = currentCycle
    ? `${currentCycle.year}-${String(currentCycle.month).padStart(2, '0')}`
    : null;

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // { file, preview } awaiting confirm
  const [committing, setCommitting] = useState(false);
  const fileRef = useRef(null);
  // Schedule-vs-supply block of the Operations KPI export for the selected
  // unit + cycle month (pieces, not ₹ per customer). Shown alongside the ERP
  // reports so a month with no .xls upload still has its figures on screen.
  const [opsSvs, setOpsSvs] = useState(null);

  useEffect(() => {
    if (!currentUnit || !currentCycle) { setOpsSvs(null); return; }
    let cancelled = false;
    api.get(`/data/production/${currentCycle.year}/${currentCycle.month}?unit=${currentUnit.code}`)
      .then((r) => {
        const d = r.data?.data;
        if (cancelled || !d || d.source !== 'ops_api') { if (!cancelled) setOpsSvs(null); return; }
        const om = d.ops_metrics || {};
        setOpsSvs({ ...(om.schedule_vs_supply || {}), pct: om.schedule_vs_supply_pct, generated_at: d.ops_generated_at, parsed_at: r.data?.parsed_at });
      })
      .catch(() => { if (!cancelled) setOpsSvs(null); });
    return () => { cancelled = true; };
  }, [currentUnit, currentCycle]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`${API_BASE}/reports`);
      const list = r.data || [];
      setReports(list);
      if (list.length > 0) {
        return list[0].id || list[0]._id;
      }
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
    return null;
  }, []);

  const loadReport = useCallback(async (id, unitName) => {
    if (!id) {
      setActive(null);
      return;
    }
    setActiveLoading(true);
    try {
      const q = unitName ? `?unit=${encodeURIComponent(unitName)}` : '';
      const r = await api.get(`${API_BASE}/reports/${id}${q}`);
      setActive(r.data);
    } catch (e) {
      // 403 = unit outside access (shouldn't happen with global selector, but
      // we surface it). 404 = the report doesn't have data for this unit.
      const status = e.response?.status;
      if (status === 403 || status === 404) {
        setActive({ id, _no_data_for_unit: true, ...(e.response?.data || {}) });
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load report');
        setActive(null);
      }
    } finally {
      setActiveLoading(false);
    }
  }, []);

  // Initial load: fetch the list once. Which report is opened is decided by
  // the cycle/unit effect below, so a June upload never shows under September.
  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only the ERP uploads for the selected cycle month AND unit are relevant.
  // Legacy uploads saved without a period are never "for this month".
  const cycleReports = reports.filter(
    (r) => r.period === currentCyclePeriod && (!currentUnit || (r.unit_codes || []).includes(currentUnit.code)),
  );

  // Open the newest upload for this cycle + unit, or clear when there is none.
  useEffect(() => {
    const first = cycleReports[0];
    if (!first) { setActive(null); return; }
    const id = first.id || first._id;
    const activeId = active && (active.id || active._id);
    if (activeId !== id || active?._no_data_for_unit) loadReport(id, selectedUnitName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnitName, currentCyclePeriod, reports]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xls|xlsx)$/i.test(f.name)) {
      toast.error('Please upload the ERP .xls / .xlsx export');
      return;
    }
    if (!currentCyclePeriod) {
      toast.error('Pick a cycle month in the top-right before uploading');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append('file', f);
    fd.append('period', currentCyclePeriod);
    fd.append('dry_run', 'true'); // Guard 1: parse + preview, save nothing.
    try {
      const r = await api.post(`${API_BASE}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPending({ file: f, preview: r.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read the file');
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Commit the previously-previewed file. `replace` is sent only when the
  // preview surfaced an existing active report for the same period/unit(s).
  const confirmUpload = async () => {
    if (!pending) return;
    const hasDup = (pending.preview.existing || []).length > 0;
    setCommitting(true);
    const fd = new FormData();
    fd.append('file', pending.file);
    fd.append('period', currentCyclePeriod);
    fd.append('dry_run', 'false');
    fd.append('replace', hasDup ? 'true' : 'false');
    try {
      const r = await api.post(`${API_BASE}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(
        `Saved ${currentCyclePeriod}` + (r.data?.replaced?.length ? ' (previous report retained in history)' : ''),
      );
      setPending(null);
      await loadReports();
      if (r.data?.id) await loadReport(r.data.id, selectedUnitName);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Save failed');
    }
    setCommitting(false);
  };

  const onPickReport = async (id) => {
    await loadReport(id, selectedUnitName);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this Supply report?')) return;
    try {
      await api.delete(`${API_BASE}/reports/${id}`);
      toast.success('Report deleted');
      setActive(null);
      await loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <Loading />;

  if (!currentUnit || !selectedUnitName) {
    return (
      <Empty
        title="No unit selected"
        hint="Pick a unit from the selector in the top-right to view its Schedule vs Supply."
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span>
            Supply Chain — Schedule vs Supply{' '}
            <span className="aampl-gold-text">({currentUnit.name})</span>
          </span>
        }
        subtitle="Figures come from the Operations KPI export uploaded in the Upload Center for the selected unit and month. The ERP Pending Sales .xls is optional — it only adds the customer-wise split, which the export does not carry."
        actions={
          canUpload ? (
            <label className="btn-ghost text-sm cursor-pointer" data-testid="btn-sc-upload">
              <Upload size={14} className="inline mr-1" />
              {busy ? 'Parsing…' : 'Upload ERP .xls (optional)'}
              <input
                ref={fileRef}
                type="file"
                accept=".xls,.xlsx"
                className="hidden"
                onChange={onPickFile}
                disabled={busy}
                data-testid="sc-file-input"
              />
            </label>
          ) : (
            <div className="text-xs text-slate-500">Read-only — only admin / management may upload.</div>
          )
        }
      />

      {opsSvs ? (
        <OpsExportCard svs={opsSvs} unitName={currentUnit.name} period={currentCyclePeriod} />
      ) : (
        <div className="px-4 py-3 rounded border border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="ops-svs-missing">
          <AlertTriangle size={14} className="inline mr-1" />
          No Operations KPI export for {currentUnit.name} / {currentCyclePeriod}. Upload the month&apos;s KPI export JSON in the <strong>Upload Center</strong> (Production) to fetch schedule vs supply automatically.
        </div>
      )}

      <div className="flex items-baseline justify-between">
        <h3 className="text-xs uppercase tracking-wider text-slate-500">
          Customer-wise split — ERP Pending Sales upload for {currentCyclePeriod} ({currentUnit.name})
        </h3>
        <span className="text-[11px] text-slate-500">Not in the KPI export • manual .xls only</span>
      </div>

      {cycleReports.length === 0 ? (
        <div className="px-4 py-3 rounded border border-dashed border-[var(--aampl-border)] bg-white text-xs text-slate-500" data-testid="sc-no-cycle-reports">
          No ERP Pending Sales report uploaded for {currentUnit.name} / {currentCyclePeriod}. The unit totals above are complete without it; upload the .xls only if the customer-wise table is needed for this month.
          {reports.length > 0 && ` (${reports.length} upload${reports.length === 1 ? '' : 's'} exist for other months or units.)`}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Reports sidebar (selecting a report does not change which unit is shown — the global selector does) */}
          <div className="lg:col-span-1 space-y-2">
            <h3 className="text-xs uppercase tracking-wider text-slate-500">Uploads for this month</h3>
            {cycleReports.map((r) => {
              const id = r.id || r._id;
              const isActive = active && (active.id || active._id) === id;
              return (
                <button
                  key={id}
                  onClick={() => onPickReport(id)}
                  className={`w-full text-left p-3 rounded border transition-colors ${
                    isActive
                      ? 'border-[var(--aampl-primary)] bg-[var(--aampl-primary-light)]'
                      : 'border-[var(--aampl-border)] hover:bg-slate-50'
                  }`}
                  data-testid={`report-card-${id}`}
                >
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet size={14} className="text-slate-500 shrink-0" />
                    <div className="text-sm font-medium truncate">{r.filename || 'report'}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">{formatDateTime(r.uploaded_at)}</div>
                  <div className="text-[11px] text-slate-600 mt-1">
                    {r.totals?.line_count || 0} lines • {r.totals?.customers || 0} customers
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(r.unit_codes || []).map((c) => (
                      <span
                        key={c}
                        className={`text-[10px] px-1.5 py-0.5 rounded ${
                          c === currentUnit.code
                            ? 'bg-[var(--aampl-navy)] text-white font-semibold'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Active report (only the selected unit's slice) */}
          <div className="lg:col-span-3 space-y-4">
            {activeLoading ? (
              <Loading />
            ) : !active ? (
              <Empty title="Select a report" hint="Pick one from the left to see details." />
            ) : (
              <ActiveReport
                doc={active}
                selectedUnitName={selectedUnitName}
                currentUnitFullName={currentUnit.name}
                isAdmin={isAdmin}
                onDelete={() => remove(active.id || active._id)}
              />
            )}
          </div>
        </div>
      )}

      {pending && (
        <ConfirmUploadModal
          period={pending.preview.period}
          preview={pending.preview}
          committing={committing}
          onCancel={() => setPending(null)}
          onConfirm={confirmUpload}
        />
      )}
    </div>
  );
}

function ConfirmUploadModal({ period, preview, committing, onCancel, onConfirm }) {
  const existing = preview.existing || [];
  const hasDup = existing.length > 0;
  const fmtMoney = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  return (
    <div
      className="fixed inset-0 bg-black/45 flex items-center justify-center z-50"
      onClick={committing ? undefined : onCancel}
      data-testid="confirm-upload-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl p-6 space-y-3"
        style={{ maxHeight: '85vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold aampl-navy-text">
          Confirm upload
        </h3>
        <p className="text-sm text-slate-700">
          You are saving this as <strong>{period}</strong>. The ERP export has no date inside it —
          confirm this file is the right month before saving.
        </p>

        {Object.entries(preview.by_unit_summary || {}).map(([u, v]) => (
          <div
            key={u}
            className="border border-[var(--aampl-border)] rounded-lg p-3"
            data-testid={`preview-${v.unit_code || u}`}
          >
            <div className="font-bold aampl-navy-text">{u}</div>
            <div className="text-sm text-slate-700">
              {v.line_count} lines · {v.customers} customers · Schedule {fmtMoney(v.schedule_amount)} ·
              Sale {fmtMoney(v.sale_amount)} · <strong>{v.sale_pct}%</strong>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Top:{' '}
              {(v.top_customers || []).map((c, i) => (
                <span key={i}>
                  {c.customer} ({fmtMoney(c.sale_amount)}){i < (v.top_customers || []).length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          </div>
        ))}

        {preview.ignored_count > 0 && (
          <div className="text-xs text-slate-500">
            {preview.ignored_count} names ignored (not in Customer Master).
          </div>
        )}

        {hasDup && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
            data-testid="dup-warning"
          >
            <div className="font-semibold">
              ⚠ {period} already has a report for these unit(s).
            </div>
            <ul className="list-disc list-inside mt-1 space-y-1">
              {existing.map((x) => (
                <li key={x.id}>
                  {x.filename} — {x.uploaded_by_name || 'unknown'} ·{' '}
                  {x.uploaded_at ? new Date(x.uploaded_at).toLocaleString() : ''} ·{' '}
                  {(x.units || []).join(', ')} · {fmtMoney(x.totals?.sale_amount)}
                </li>
              ))}
            </ul>
            <div className="mt-2">
              Saving will make your new file the active report (the previous one is retained in
              history for audit; <code>cycle-autofill</code> uses the latest).
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--aampl-border)]">
          <button className="btn-ghost" onClick={onCancel} disabled={committing} data-testid="btn-cancel-upload">
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={onConfirm}
            disabled={committing}
            data-testid="btn-confirm-upload"
          >
            {committing
              ? 'Saving…'
              : hasDup
                ? 'Replace active report'
                : `Confirm — save ${period}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActiveReport({ doc, selectedUnitName, currentUnitFullName, isAdmin, onDelete }) {
  const byUnit = doc.by_unit || {};
  const bucket = byUnit[selectedUnitName] || null;
  const ignored = doc.ignored || [];

  return (
    <>
      <Card
        title={doc.filename || 'Report'}
        action={
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">
              Uploaded {formatDateTime(doc.uploaded_at)} by {doc.uploaded_by_name || '—'}
            </span>
            {isAdmin && doc.id && (
              <button
                className="text-xs text-red-700 hover:underline"
                onClick={onDelete}
                data-testid="btn-delete-report"
              >
                <Trash2 size={12} className="inline" /> Delete
              </button>
            )}
          </div>
        }
      >
        {!bucket ? (
          <div className="py-6 text-center" data-testid="no-unit-data">
            <AlertTriangle size={20} className="inline mb-2 text-amber-500" />
            <div className="text-sm text-slate-600">
              No Schedule vs Supply rows for <strong>{currentUnitFullName}</strong> in this report.
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Switch the unit selector in the top-right, or upload an export that includes this unit.
            </div>
          </div>
        ) : (
          <UnitBody bucket={bucket} />
        )}
      </Card>

      {ignored.length > 0 && (
        <Card
          title={`Ignored names (${ignored.length})`}
          action={
            <span className="text-xs text-slate-500">
              Not in Customer Master — none counted, none silently dropped
            </span>
          }
        >
          <div className="mb-2 text-xs text-slate-600 flex items-center gap-1">
            <AlertTriangle size={12} className="text-amber-600" />
            These raw "Customer" strings did not match any active alias. Add aliases in the Customer Master to roll them in.
          </div>
          <div className="aampl-table-wrap">
            <table className="aampl-table text-xs" data-testid="sc-ignored-table">
              <thead>
                <tr>
                  <th>Raw customer string</th>
                  <th className="text-right">Lines</th>
                </tr>
              </thead>
              <tbody>
                {ignored.map((ig, i) => (
                  <tr key={i}>
                    <td className="font-mono">{ig.raw_customer}</td>
                    <td className="text-right">{ig.line_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

function UnitBody({ bucket }) {
  const t = bucket.totals || {};
  const summary = bucket.summary || [];
  const pct = t.sale_pct || 0;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid={`unit-kpis-${bucket.unit_code || ''}`}>
        <KpiTile label="Schedule" value={formatRupees(t.schedule_amount)} />
        <KpiTile label="Sale" value={formatRupees(t.sale_amount)} />
        <KpiTile
          label="Sale %"
          value={t.schedule_amount ? `${pct.toFixed(1)}%` : '–'}
          tone={pct >= 95 ? 'green' : pct >= 80 ? 'amber' : 'red'}
        />
        <KpiTile label="Lines" value={`${formatNumber(t.line_count)} • ${t.customers || 0} customers`} />
      </div>

      {summary.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center">No customer rows.</div>
      ) : (
        <div className="aampl-table-wrap">
          <table className="aampl-table" data-testid={`unit-summary-${bucket.unit_code || ''}`}>
            <thead>
              <tr>
                <th>Customer</th>
                <th className="text-right">Schedule</th>
                <th className="text-right">Sale</th>
                <th className="text-right">Sale %</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((s, i) => (
                <tr key={i}>
                  <td className="font-medium">{s.customer}</td>
                  <td className="text-right">{formatRupees(s.schedule_amount)}</td>
                  <td className="text-right">{formatRupees(s.sale_amount)}</td>
                  <td className="text-right">
                    <SectionBadge
                      status={s.sale_pct >= 95 ? 'green' : s.sale_pct >= 80 ? 'amber' : 'red'}
                      label={`${s.sale_pct}%`}
                    />
                  </td>
                </tr>
              ))}
              <tr className="font-bold border-t-2 border-[var(--aampl-navy)]">
                <td>TOTAL</td>
                <td className="text-right">{formatRupees(t.schedule_amount)}</td>
                <td className="text-right">{formatRupees(t.sale_amount)}</td>
                <td className="text-right">{t.schedule_amount ? `${pct.toFixed(1)}%` : '–'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function KpiTile({ label, value, tone = 'navy' }) {
  const toneClass =
    tone === 'green' ? 'text-emerald-700' :
    tone === 'amber' ? 'text-amber-700' :
    tone === 'red' ? 'text-red-700' :
    'aampl-navy-text';
  return (
    <div className="aampl-card p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-bold mt-1 ${toneClass}`}>
        {value}
      </div>
    </div>
  );
}

// Operations KPI export block for the cycle month. The export reports pieces
// (schedule, dispatched, pending) plus one dispatch value for the unit — no
// per-customer or per-SO rows, which only the ERP .xls carries.
function OpsExportCard({ svs, unitName, period }) {
  if (!svs) return null;
  const has = ['monthly_total', 'sched_to_date', 'dispatched', 'parts', 'dispatch_value_inr'].some((k) => Number(svs[k]) > 0);
  if (!has) return null;
  const pctMonth = svs.pct_month ?? svs.pct;
  const tone = (p) => (p >= 95 ? 'green' : p >= 80 ? 'amber' : 'red');
  const stat = (label, value) => (
    <div key={label} className="border border-[var(--aampl-border)] rounded px-3 py-2 flex items-baseline justify-between gap-2 text-xs">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold aampl-navy-text">{value}</span>
    </div>
  );
  return (
    <Card
      title={`Schedule vs Supply — ${unitName} / ${period}`}
      action={<span className="text-xs text-slate-500">Fetched from the Operations KPI export • generated {formatDateTime(svs.generated_at)}</span>}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3" data-testid="ops-svs-kpis">
        <div className="kpi-card navy"><div className="text-xs uppercase tracking-wider text-slate-500">Monthly schedule</div><div className="text-2xl font-bold aampl-navy-text mt-1">{formatNumber(svs.monthly_total)}</div></div>
        <div className="kpi-card navy"><div className="text-xs uppercase tracking-wider text-slate-500">Dispatched</div><div className="text-2xl font-bold aampl-navy-text mt-1">{formatNumber(svs.dispatched)}</div></div>
        <div className={`kpi-card ${tone(pctMonth)}`}><div className="text-xs uppercase tracking-wider text-slate-500">Schedule vs supply (month)</div><div className="text-2xl font-bold aampl-navy-text mt-1">{pctMonth ?? '–'}%</div></div>
        <div className="kpi-card navy"><div className="text-xs uppercase tracking-wider text-slate-500">Dispatch value</div><div className="text-2xl font-bold aampl-navy-text mt-1">{formatRupees(svs.dispatch_value_inr)}</div></div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stat('Schedule to date (pcs)', formatNumber(svs.sched_to_date))}
        {stat('Pending (pcs)', formatNumber(svs.pending))}
        {stat('To-date %', svs.pct_to_date === null || svs.pct_to_date === undefined ? '–' : `${svs.pct_to_date}%`)}
        {stat('Parts scheduled', formatNumber(svs.parts))}
      </div>
      <div className="text-[11px] text-slate-500 mt-2">
        Schedule, dispatched and pending are pieces; dispatch value is ₹ for the whole unit. The export carries no customer-wise or SO-wise rows.
      </div>
    </Card>
  );
}
